import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { evaluatePermission } from "redux-store/abac/actions";
import { selectIsPermitted } from "redux-store/abac/selectors";
import abacCache from "utils/abacCache";
import permissionBatcher from "utils/permissionBatcher";

/**
 * Check if user is Super Admin
 */
const isSuperAdmin = (user) => {
  return user?.userDetails?.role === "SUPER_ADMIN";
};

/**
 * Hook for checking ABAC permissions
 *
 * @param {string} resourceType - Type of resource (e.g., "device", "user", "organization")
 * @param {string} action - Action to perform (e.g., "read", "write", "delete")
 * @param {string} [resourceId] - Optional specific resource ID
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.autoEvaluate=true] - Automatically evaluate on mount
 * @param {boolean} [options.useBatcher=false] - Use request batching
 * @param {Object} [options.context] - Additional context for evaluation
 *
 * @returns {Object} Permission state and methods
 * @returns {boolean} granted - Whether permission is granted
 * @returns {boolean} loading - Whether evaluation is in progress
 * @returns {Error|null} error - Error if evaluation failed
 * @returns {Function} evaluate - Function to manually trigger evaluation
 * @returns {Function} refresh - Function to refresh (bypass cache)
 */
export const useAbacPermission = (
  resourceType,
  action,
  resourceId = null,
  options = {}
) => {
  const {
    autoEvaluate = true,
    useBatcher = false,
    context: additionalContext = null,
  } = options;

  const dispatch = useDispatch();
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [granted, setGranted] = useState(null);
  const mountedRef = useRef(true);
  // Bumped by every evaluation (and cache hit) so only the latest one may
  // update state: a late answer for the previous resource/action must not
  // overwrite, or leave loading, for the current one.
  const requestSeqRef = useRef(0);

  // Get user from auth state to check for Super Admin
  const { user } = useSelector((state) => state.auth);
  const superAdmin = useMemo(() => isSuperAdmin(user), [user]);

  // Get cached permission from Redux store
  const cachedPermission = useSelector((state) =>
    selectIsPermitted(resourceType, action, resourceId)(state)
  );

  // Loading reflects THIS hook's evaluation only (Super Admin never loading).
  // It used to OR in the store-wide `loading.permission` flag, which is true
  // while ANY component's evaluation is pending, so a page guard could keep
  // spinning on a check that had nothing to do with it.
  const loading = superAdmin ? false : localLoading;

  /**
   * Evaluate permission using the batcher
   */
  const evaluateWithBatcher = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    const isCurrent = () => mountedRef.current && seq === requestSeqRef.current;
    setLocalLoading(true);
    setLocalError(null);

    try {
      const result = await permissionBatcher.add({
        resourceType,
        action,
        resourceId,
        context: additionalContext,
      });

      if (!isCurrent()) return;

      setGranted(result.granted === true);
      setLocalLoading(false);

      return result;
    } catch (error) {
      if (!isCurrent()) return;

      setLocalError(error);
      setLocalLoading(false);
      setGranted(false);

      throw error;
    }
  }, [resourceType, action, resourceId, additionalContext]);

  /**
   * Evaluate permission using direct API call
   */
  const evaluateDirect = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    const isCurrent = () => mountedRef.current && seq === requestSeqRef.current;
    setLocalLoading(true);
    setLocalError(null);

    try {
      const result = await dispatch(
        evaluatePermission({
          resourceType,
          action,
          resourceId,
          context: additionalContext,
        })
      ).unwrap();

      if (!isCurrent()) return;

      // API returns { success, data: { granted } } - extract from data object
      const permissionGranted = result?.result?.data?.granted === true || result?.result?.granted === true;
      setGranted(permissionGranted);
      setLocalLoading(false);

      return result;
    } catch (error) {
      if (!isCurrent()) return;

      setLocalError(error);
      setLocalLoading(false);
      setGranted(false);

      throw error;
    }
  }, [dispatch, resourceType, action, resourceId, additionalContext]);

  /**
   * Main evaluate function
   */
  const evaluate = useCallback(async () => {
    // Check cache first
    const cacheKey = abacCache.constructor.generateKey(
      resourceType,
      action,
      resourceId
    );
    const cached = abacCache.get(cacheKey);

    if (cached !== null) {
      // Supersedes any evaluation still in flight, so settle loading here.
      requestSeqRef.current += 1;
      setLocalLoading(false);
      setLocalError(null);
      setGranted(cached.granted === true);
      return cached;
    }

    // Use appropriate evaluation method
    if (useBatcher) {
      return evaluateWithBatcher();
    } else {
      return evaluateDirect();
    }
  }, [resourceType, action, resourceId, useBatcher, evaluateWithBatcher, evaluateDirect]);

  /**
   * Refresh permission (bypass cache)
   */
  const refresh = useCallback(async () => {
    // Invalidate cache for this specific permission
    const cacheKey = abacCache.constructor.generateKey(
      resourceType,
      action,
      resourceId
    );
    abacCache.invalidate(cacheKey);

    // Re-evaluate
    return evaluate();
  }, [resourceType, action, resourceId, evaluate]);

  // Update granted state when cached permission changes
  useEffect(() => {
    if (cachedPermission !== null) {
      setGranted(cachedPermission);
    }
  }, [cachedPermission]);

  // Track mount on its own. The auto-evaluate effect below re-runs whenever
  // its inputs change; it used to clear this flag in its cleanup and never
  // set it back, so the re-run's own answer was dropped as "unmounted" and
  // loading stayed true for good (AbacProtectedRoute spinning forever).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Auto-evaluate on mount if enabled
  useEffect(() => {
    if (autoEvaluate && resourceType && action) {
      evaluate().catch((error) => {
        // Error already handled in evaluate function
        console.error("Permission evaluation failed:", error);
      });
    }
  }, [autoEvaluate, resourceType, action, resourceId, evaluate]);

  // Super Admin always has access - bypass ABAC checks
  if (superAdmin) {
    return {
      granted: true,
      loading: false,
      error: null,
      evaluate: async () => ({ granted: true }),
      refresh: async () => ({ granted: true }),
    };
  }

  return {
    granted,
    loading,
    error: localError,
    evaluate,
    refresh,
  };
};

export default useAbacPermission;
